// ============================================================================
// packVersionHistory.js — PURE derivation of Pack Version History rows
// ----------------------------------------------------------------------------
// Derives a read-only lifecycle view of one logical Pack from its raw version
// documents (draft / active / archived). No Firestore, no React, no side
// effects — pure functions only, so it is node-testable in isolation
// (see packVersionHistory.verify.js).
//
// Lifecycle is DERIVED from existing pack-doc fields + the Phase-2 lineage
// fields (supersedes*/supersededBy*). We never fabricate a supersede link that
// isn't present in the data, and we never log per-edit "modified" events.
// ============================================================================

export const PACK_HISTORY_EVENT = {
  DRAFT: 'DRAFT',
  RELEASED: 'RELEASED',
  RELEASED_NEW_VERSION: 'RELEASED_NEW_VERSION',
  RETIRED: 'RETIRED',
};

const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;

// Local, dependency-free semver compare for tie-breaking only. Kept here (rather
// than importing compareSemver from store.packs.js) so this module stays pure —
// store.packs.js imports the Firebase SDK. Returns -1 | 0 | 1, or 0 when either
// side is not parseable (no tie-break preference).
const compareSemverSafe = (a, b) => {
  const pa = SEMVER_RE.exec(typeof a === 'string' ? a.trim() : '');
  const pb = SEMVER_RE.exec(typeof b === 'string' ? b.trim() : '');
  if (!pa || !pb) return 0;
  for (let i = 1; i <= 3; i += 1) {
    const na = Number(pa[i]);
    const nb = Number(pb[i]);
    if (na !== nb) return na < nb ? -1 : 1;
  }
  return 0;
};

// Normalize a timestamp-ish value to epoch millis (or null). Handles Firestore
// Timestamp (toMillis() or {seconds,nanoseconds}), Date, number, and null.
export const toMillis = (ts) => {
  if (ts == null) return null;
  if (typeof ts === 'number') return ts;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
  return null;
};

// Derive a single history row from one raw pack version document.
const deriveRow = (doc) => {
  const d = doc || {};
  const packDocId = d.id || d.packDocId || null;
  const packId = d.packId || (packDocId ? packDocId.split('__')[0] : null);
  const versionId = d.versionId || (packDocId && packDocId.includes('__') ? packDocId.slice(packDocId.lastIndexOf('__') + 2) : '');
  const status = d.status || 'draft';
  const supersedesVersionId = d.supersedesVersionId ?? null;
  const supersededByVersionId = d.supersededByVersionId ?? null;

  let eventType;
  let eventLabel;
  let occurredAt;

  if (status === 'active') {
    eventType = supersedesVersionId ? PACK_HISTORY_EVENT.RELEASED_NEW_VERSION : PACK_HISTORY_EVENT.RELEASED;
    eventLabel = supersedesVersionId ? 'Published new version' : 'Published';
    occurredAt = d.publishedAt ?? d.createdAt ?? d.updatedAt ?? null;
  } else if (status === 'archived') {
    eventType = PACK_HISTORY_EVENT.RETIRED;
    eventLabel = supersededByVersionId ? `Archived · superseded by v${supersededByVersionId}` : 'Archived';
    occurredAt = d.archivedAt ?? d.updatedAt ?? null;
  } else {
    // draft (and any unknown status falls back to a neutral draft row)
    eventType = PACK_HISTORY_EVENT.DRAFT;
    eventLabel = 'Draft';
    occurredAt = d.updatedAt ?? d.createdAt ?? null;
  }

  return {
    packDocId,
    packId,
    versionId,
    status,
    eventType,
    eventLabel,
    occurredAt,
    actorUid: d.createdByUid ?? null,
    supersedesVersionId,
    supersededByVersionId,
    isCurrentActive: status === 'active',
  };
};

/**
 * Derive the full version-history row list for one logical Pack.
 * Sorted newest-first by occurredAt (nulls last), tie-broken by semver desc.
 * @param {Array<object>} docs - raw pack version docs (from getPackVersions)
 * @returns {Array<object>} history rows (see deriveRow shape)
 */
export const derivePackVersionRows = (docs) => {
  const rows = (Array.isArray(docs) ? docs : []).map(deriveRow);
  rows.sort((a, b) => {
    const ma = toMillis(a.occurredAt);
    const mb = toMillis(b.occurredAt);
    if (ma != null && mb != null && ma !== mb) return mb - ma; // newest first
    if (ma == null && mb != null) return 1;  // nulls last
    if (ma != null && mb == null) return -1;
    return -compareSemverSafe(a.versionId, b.versionId); // tie-break: semver desc
  });
  return rows;
};

/** Count rows that are currently active. */
export const countActiveVersions = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => r.isCurrentActive).length;

/** True if more than one active version exists (data anomaly — flag, do not fix). */
export const hasMultipleActiveVersions = (rows) => countActiveVersions(rows) > 1;

export default {
  PACK_HISTORY_EVENT,
  toMillis,
  derivePackVersionRows,
  countActiveVersions,
  hasMultipleActiveVersions,
};
